import { Icon } from "@/components/Atoms/Icon"
import { imagebossSetWidth } from "@/utils/imageboss"
import { Button } from "@/components/ui/button"
import { EventoDataType } from "@/types/events"
import { useState } from "react"

interface EventCardProps {
  evento: EventoDataType
  progresso: number
  autorizados: number
  recusados: number
  pendentes: number
  currentOpened: number | null
  children?: React.ReactNode
  onCardClick: (event: EventoDataType) => void
  onTechnicalFileClick?: (e: React.MouseEvent) => void
  hideActions?: boolean
}

export function EventCard({
  evento,
  progresso,
  autorizados,
  recusados,
  pendentes,
  currentOpened,
  children,
  onCardClick,
  onTechnicalFileClick,
  hideActions,
}: EventCardProps) {
  const [isOpened, setIsOpened] = useState(false)

  const toggleIsOpened = () => {
    setIsOpened(!isOpened)
  }

  const handleCardClick = () => {
    toggleIsOpened()
    onCardClick(evento)
  }

  return (
    <div
      onClick={handleCardClick}
      key={evento.id}
      className={`group cursor-pointer space-y-3 overflow-hidden rounded-lg border border-border bg-surface transition-all duration-500 ${currentOpened === evento.id ? "border-orange-500 hover:border-orange-500" : "hover:border-zinc-500"}`}
    >
      <div className="flex flex-wrap items-center gap-4 p-4 pb-1">
        <div className="flex flex-[2_1] items-center gap-4">
          <div className="left-0 top-0 flex h-12 w-[100px] min-w-[100px] items-center justify-center overflow-hidden rounded-xl border">
            {evento.ime_foto_url || evento.eve_foto_url ? (
              <img
                src={imagebossSetWidth(evento.ime_foto_url || evento.eve_foto_url, 200) ?? (evento.ime_foto_url || evento.eve_foto_url)!}
                alt={evento.descricao}
                className="size-full object-cover"
              />
            ) : (
              <i className="fi fi-ts-calendar-image text-muted"></i>
            )}
          </div>
          <h3 className="font-semibold text-foreground">{evento.descricao}</h3>
        </div>

        <div className="flex-[2_1] p-4">
          <div className="relative h-5 w-full overflow-hidden rounded-full bg-gray-950 p-1 shadow-sm">
            <div
              className="h-full rounded-full bg-gradient-to-r from-slate-900 to-orange-500"
              style={{ width: progresso + "%" }}
            />
            <div className="absolute left-0 top-0 size-full rounded-full border-t border-x-transparent border-b-gray-900 border-t-gray-700 bg-gradient-to-t from-black/10 to-gray-400/20" />
          </div>
          <div className="flex justify-center gap-2 text-xs text-muted-foreground">
            <div className="flex items-center gap-2 text-nowrap">
              <span className="size-2 rounded-full bg-green-500" />
              <span>Aprovados: {autorizados}</span>
            </div>
            <div className="flex items-center gap-2 text-nowrap">
              <span className="size-2 rounded-full bg-red-500" />
              <span>Recusados: {recusados}</span>
            </div>
            <div className="flex items-center gap-2 text-nowrap">
              <span className="size-2 rounded-full bg-yellow-500" />
              <span>Pendentes: {pendentes}</span>
            </div>
          </div>
        </div>
        <div className="flex flex-[2] items-center justify-center gap-4">
          <div className="text-sm text-muted-foreground">
            <p>Data Inicial</p>
            <span>{new Date(evento.data_inicio).toLocaleDateString("pt-BR")}</span>
            <span>-</span>
            <span>
              {new Date(evento.data_inicio).toLocaleTimeString("pt-BR", {
                hour: "2-digit",
                minute: "2-digit",
              }) + "h"}
            </span>
          </div>
          <i className="fi fi-ts-arrow-right" />
          <div className="text-sm text-muted-foreground">
            <p>Data Final</p>
            <span>{new Date(evento.data_fim).toLocaleDateString("pt-BR")}</span>
            <span>-</span>
            <span>
              {new Date(evento.data_fim).toLocaleTimeString("pt-BR", {
                hour: "2-digit",
                minute: "2-digit",
              }) + "h"}
            </span>
          </div>
        </div>

        <div className="flex flex-[1_1] flex-col items-end">
          {!hideActions && evento.url_ficha_tecnica && onTechnicalFileClick && (
            <Button
              onClick={(e) => {
                e.stopPropagation()
                onTechnicalFileClick(e)
              }}
              variant="link"
              size="sm"
              className="flex w-full items-center justify-end gap-2 text-xs font-semibold uppercase text-purple-500 hover:text-white hover:no-underline"
            >
              Ficha técnica
              <Icon
                state="elegant"
                name="assessment"
                size={15}
              />
            </Button>
          )}
          <p className="text-muted-foreground">Limite de inscrição</p>
          <div className="inline rounded-full bg-red-500/10 px-2 text-red-500">
            {new Date(evento.data_limite).toLocaleDateString("pt-BR")}
          </div>
        </div>
      </div>

      <div
        onClick={(e) => e.stopPropagation()}
        className={`h-full bg-zinc-900 ${currentOpened === evento.id ? "max-h-[400px] border-t p-4" : "max-h-0"} w-full cursor-default overflow-auto transition-all duration-500`}
      >
        {children}
      </div>
    </div>
  )
}
